import { useState, type CSSProperties } from 'react'
import { Outlet } from 'react-router-dom'
import {
  RAIL_COLLAPSED, readRailCollapsed, readRailWidth, saveRailCollapsed, saveRailWidth,
} from '../lib/rail-width.ts'
import { Sidebar } from './Sidebar.tsx'
import { SidebarResizer } from './SidebarResizer.tsx'
import { TopBar } from './TopBar.tsx'

/**
 * Layout route for every signed-in page: a full-height nav rail on the left, and
 * a main column holding the topbar, the routed page, and the footer. The rail
 * carries the brand mark, so TopBar is only the theme control + user chip.
 *
 * The rail's width and folded state live here because the grid that reads them
 * does. Both are read once on mount rather than on every render — nothing else
 * writes the stored values.
 */
export function AppShell() {
  const [railWidth, setRailWidth] = useState(readRailWidth)
  const [collapsed, setCollapsed] = useState(readRailCollapsed)

  const toggleCollapsed = () => {
    setCollapsed((wasCollapsed) => {
      saveRailCollapsed(!wasCollapsed)
      return !wasCollapsed
    })
  }

  // Folding overrides the chosen width without discarding it, so opening the
  // rail again returns it to the width it had.
  const width = collapsed ? RAIL_COLLAPSED : railWidth

  return (
    <div
      className={collapsed ? 'shell shell--rail-folded' : 'shell'}
      style={{ '--rail-w': `${width}px` } as CSSProperties}
    >
      <Sidebar collapsed={collapsed} onToggle={toggleCollapsed}>
        {/* A folded rail has no width to choose, so the handle goes with it. */}
        {!collapsed && (
          <SidebarResizer width={railWidth} onChange={setRailWidth} onCommit={saveRailWidth} />
        )}
      </Sidebar>
      <div className="shell__main">
        <TopBar />
        <Outlet />
      </div>
    </div>
  )
}
